import Text from "../../../components/Text";
import { OnMouseOver } from "../../../layouts/SupportZone/constants";
import SpecialSupport from "../../../layouts/SupportZone/SpecialSupport";
import { MARGINS_BY_BLOCK_TYPE } from "../../constants";
import type { BodyBlock } from "../../schema/blocks/body";
import Editable from "../components/Editable";
import BlockOptionsPanel from "../options/BlockOptionsPanel";
import { handleLinearNavigation } from "../utilities/navigation";
import type { BlockEditorProps } from "./types";

export default function BodyBlockEditor(props: BlockEditorProps<BodyBlock>) {
    const { block, onChange, autoFocus, controls } = props;
    return (
        <SpecialSupport
            enabled={OnMouseOver}
            content={
                <BlockOptionsPanel
                    onInsert={controls.insertBlock}
                    onRemove={controls.removeBlock}
                />
            }
        >
            <Text variant="body" css={MARGINS_BY_BLOCK_TYPE[block.type]}>
                <Editable
                    mode="html"
                    value={block.html}
                    placeholder="Paragraphe"
                    autoFocus={autoFocus}
                    onChange={(html) => {
                        const heading = consumeHeadingShortcut(html);
                        if (heading !== null) {
                            onChange({
                                id: block.id,
                                type: "title",
                                text: heading.text,
                                anchor: null,
                                level: heading.level,
                            });
                            return;
                        }
                        const listHtml = consumeBulletShortcut(html);
                        if (listHtml !== null) {
                            onChange({
                                id: block.id,
                                type: "listItem",
                                html: listHtml,
                                ordered: false,
                                depth: 0,
                            });
                            return;
                        }
                        onChange({ ...block, html });
                    }}
                    allowLineBreak={false}
                    onEnter={controls.splitBlock}
                    onBackspace={controls.mergeWithPrevious}
                    onPasteBlocks={controls.pasteBlocks}
                    onKeyDown={(event) => {
                        if (handleLinearNavigation(event)) controls.closeOptions();
                    }}
                />
            </Text>
        </SpecialSupport>
    );
}

function consumeHeadingShortcut(html: string): { text: string; level: 1 | 2 | 3 } | null {
    const template = document.createElement("template");
    template.innerHTML = html;
    const text = template.content.textContent ?? "";
    const match = text.match(/^(#{1,3})(?: |\u00a0)/);
    if (!match || !match[1]) return null;
    return {
        text: text.slice(match[0].length),
        level: match[1].length as 1 | 2 | 3,
    };
}

function consumeBulletShortcut(html: string): string | null {
    const template = document.createElement("template");
    template.innerHTML = html;
    const text = template.content.textContent ?? "";
    if (!text.startsWith("- ") && !text.startsWith("-\u00a0")) return null;
    consumeLeadingCharacters(template.content, 2);
    return template.innerHTML;
}

function consumeLeadingCharacters(root: ParentNode, count: number) {
    let remaining = count;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const toRemove: Text[] = [];
    while (remaining > 0 && walker.nextNode()) {
        const node = walker.currentNode as Text;
        if (node.data.length <= remaining) {
            remaining -= node.data.length;
            toRemove.push(node);
        } else {
            node.data = node.data.slice(remaining);
            remaining = 0;
        }
    }
    for (const node of toRemove) node.remove();
}
